import { Component } from '@theme/component';

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Hero slideshow for the MALÁ home page. Slides cross with a curtain reveal; autoplay is driven by
 * the active dot's CSS progress animation, so hover, focus and the pause button freeze it for free.
 *
 * @typedef {object} HeroRefs
 * @property {HTMLElement[]} slides
 * @property {HTMLButtonElement[]} [dots]
 * @property {HTMLElement} [current]
 * @property {HTMLButtonElement} [toggle]
 * @property {HTMLElement} [status]
 *
 * @extends {Component<HeroRefs>}
 */
class MalaHeroSlider extends Component {
  requiredRefs = ['slides'];

  #index = 0;
  /** @type {AbortController | undefined} */
  #abort;
  /** @type {number | undefined} */
  #leaveTimer;
  #pointerStart = /** @type {{ x: number, y: number } | null} */ (null);

  connectedCallback() {
    super.connectedCallback();

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#index = Math.max(0, this.refs.slides.findIndex((slide) => slide.classList.contains('is-active')));
    if (prefersReducedMotion()) this.removeAttribute('autoplay');

    this.addEventListener('animationend', this.#onProgressEnd, { signal });
    this.addEventListener('keydown', this.#onKeydown, { signal });
    this.addEventListener('pointerdown', this.#onPointerDown, { signal });
    this.addEventListener('pointerup', this.#onPointerUp, { signal });
    document.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });

    this.#syncMedia();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    window.clearTimeout(this.#leaveTimer);
  }

  next() {
    this.goTo(this.#index + 1, 'next');
  }

  previous() {
    this.goTo(this.#index - 1, 'prev');
  }

  /**
   * @param {MouseEvent} event
   */
  selectSlide(event) {
    const dot = event.target instanceof Element ? event.target.closest('[data-index]') : null;
    if (!(dot instanceof HTMLElement)) return;

    this.goTo(Number(dot.dataset.index));
  }

  togglePlayback() {
    const paused = !this.hasAttribute('data-paused');
    this.toggleAttribute('data-paused', paused);

    const { toggle } = this.refs;
    if (!toggle) return;

    toggle.setAttribute('aria-pressed', String(paused));
    toggle.setAttribute('aria-label', (paused ? toggle.dataset.labelPlay : toggle.dataset.labelPause) ?? '');
  }

  /**
   * @param {number} target
   * @param {'next' | 'prev'} [direction]
   */
  goTo(target, direction) {
    const { slides } = this.refs;
    const count = slides.length;
    const nextIndex = (target + count) % count;
    if (count < 2 || nextIndex === this.#index) return;

    const current = slides[this.#index];
    const incoming = slides[nextIndex];
    if (!current || !incoming) return;

    this.dataset.direction = direction ?? (nextIndex > this.#index ? 'next' : 'prev');

    for (const slide of slides) slide.classList.remove('is-leaving');
    current.classList.replace('is-active', 'is-leaving');
    current.inert = true;
    incoming.inert = false;
    incoming.classList.add('is-active');

    window.clearTimeout(this.#leaveTimer);
    this.#leaveTimer = window.setTimeout(() => current.classList.remove('is-leaving'), 1400);

    this.#index = nextIndex;
    this.#updateControls();
    this.#syncMedia();
  }

  #updateControls() {
    const { dots, current, status, slides } = this.refs;

    for (const [index, dot] of (dots ?? []).entries()) {
      const active = index === this.#index;
      dot.classList.toggle('is-active', active);
      if (active) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    }

    const number = String(this.#index + 1).padStart(2, '0');
    if (current) current.textContent = number;
    if (status) status.textContent = slides[this.#index]?.getAttribute('aria-label') ?? '';
  }

  #syncMedia() {
    const reduced = prefersReducedMotion();

    for (const [index, slide] of this.refs.slides.entries()) {
      for (const video of slide.querySelectorAll('video')) {
        if (index === this.#index && !reduced) video.play().catch(() => {});
        else video.pause();
      }
    }
  }

  /** @param {AnimationEvent} event */
  #onProgressEnd = (event) => {
    if (event.animationName !== 'malaHeroProgress' || !this.hasAttribute('autoplay')) return;
    this.next();
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    const rtl = getComputedStyle(this).direction === 'rtl';
    if (event.key === 'ArrowRight') rtl ? this.previous() : this.next();
    if (event.key === 'ArrowLeft') rtl ? this.next() : this.previous();
  };

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    if (event.pointerType === 'mouse') return;
    this.#pointerStart = { x: event.clientX, y: event.clientY };
  };

  /** @param {PointerEvent} event */
  #onPointerUp = (event) => {
    if (!this.#pointerStart) return;

    const deltaX = event.clientX - this.#pointerStart.x;
    const deltaY = event.clientY - this.#pointerStart.y;
    this.#pointerStart = null;
    if (Math.abs(deltaX) < 50 || Math.abs(deltaX) < Math.abs(deltaY)) return;

    deltaX < 0 ? this.next() : this.previous();
  };

  /** @param {Event} event */
  #onBlockSelect = (event) => {
    const blockId = /** @type {CustomEvent<{ blockId: string }>} */ (event).detail?.blockId;
    const index = this.refs.slides.findIndex((slide) => slide.dataset.blockId === blockId);
    if (index < 0) return;

    this.setAttribute('data-paused', '');
    this.goTo(index);
  };
}

/**
 * Horizontal product rail with snap scrolling, arrow buttons, a progress line and mouse dragging.
 *
 * @typedef {object} CarouselRefs
 * @property {HTMLElement} track
 * @property {HTMLButtonElement} [previousButton]
 * @property {HTMLButtonElement} [nextButton]
 * @property {HTMLElement} [progress]
 *
 * @extends {Component<CarouselRefs>}
 */
class MalaCarousel extends Component {
  requiredRefs = ['track'];

  /** @type {AbortController | undefined} */
  #abort;
  /** @type {ResizeObserver | undefined} */
  #resizeObserver;
  #frame = 0;
  #drag = /** @type {{ x: number, scroll: number, moved: boolean } | null} */ (null);

  connectedCallback() {
    super.connectedCallback();
    this.#listen();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#listen();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#teardown();
  }

  previous() {
    this.#step(-1);
  }

  next() {
    this.#step(1);
  }

  #listen() {
    this.#teardown();
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    const { track } = this.refs;

    track.addEventListener('scroll', this.#scheduleUpdate, { passive: true, signal });
    track.addEventListener('pointerdown', this.#onPointerDown, { signal });
    track.addEventListener('click', this.#onClick, { capture: true, signal });
    track.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
    window.addEventListener('pointermove', this.#onPointerMove, { signal });
    window.addEventListener('pointerup', this.#onPointerUp, { signal });

    this.#resizeObserver = new ResizeObserver(this.#scheduleUpdate);
    this.#resizeObserver.observe(track);
    this.#update();
  }

  #teardown() {
    this.#abort?.abort();
    this.#resizeObserver?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  get #isRtl() {
    return getComputedStyle(this).direction === 'rtl';
  }

  /** @param {number} direction */
  #step(direction) {
    const { track } = this.refs;
    const item = track.firstElementChild;
    if (!(item instanceof HTMLElement)) return;

    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const itemWidth = item.offsetWidth + gap;
    const perPage = Math.max(1, Math.floor((track.clientWidth + gap) / itemWidth));

    track.scrollBy({
      left: direction * itemWidth * perPage * (this.#isRtl ? -1 : 1),
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  }

  #scheduleUpdate = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  };

  #update() {
    const { track, previousButton, nextButton, progress } = this.refs;
    const max = track.scrollWidth - track.clientWidth;
    const position = Math.abs(track.scrollLeft);
    const ratio = track.scrollWidth ? track.clientWidth / track.scrollWidth : 1;

    this.toggleAttribute('data-static', max <= 2);
    if (previousButton) previousButton.disabled = position <= 2;
    if (nextButton) nextButton.disabled = position >= max - 2;
    if (progress) {
      progress.style.setProperty('--mala-carousel-thumb', String(Math.min(1, ratio)));
      progress.style.setProperty('--mala-carousel-progress', String(max > 0 ? position / max : 0));
    }
  }

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    this.#drag = { x: event.clientX, scroll: this.refs.track.scrollLeft, moved: false };
  };

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    if (!this.#drag) return;

    const delta = event.clientX - this.#drag.x;
    if (!this.#drag.moved && Math.abs(delta) < 6) return;

    if (!this.#drag.moved) {
      this.#drag.moved = true;
      this.setAttribute('data-dragging', '');
    }
    this.refs.track.scrollLeft = this.#drag.scroll - delta;
  };

  #onPointerUp = () => {
    if (!this.#drag) return;

    this.removeAttribute('data-dragging');
    if (!this.#drag.moved) {
      this.#drag = null;
      return;
    }
    // The click that follows a drag fires after pointerup; let #onClick swallow it first.
    window.setTimeout(() => (this.#drag = null));
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (!this.#drag?.moved) return;

    event.preventDefault();
    event.stopPropagation();
    this.#drag = null;
  };
}

/**
 * Background video with a visible pause control; stays paused for reduced-motion users.
 *
 * @typedef {object} VideoRefs
 * @property {HTMLVideoElement} [video]
 * @property {HTMLButtonElement} [toggle]
 *
 * @extends {Component<VideoRefs>}
 */
class MalaBackgroundVideo extends Component {
  /** @type {IntersectionObserver | undefined} */
  #observer;
  #userPaused = false;

  /** `video_tag` output cannot carry a ref, so fall back to the first descendant video. */
  get #video() {
    return this.refs.video ?? this.querySelector('video');
  }

  connectedCallback() {
    super.connectedCallback();

    const video = this.#video;
    if (!video) return;

    this.#userPaused = prefersReducedMotion();
    this.#observer = new IntersectionObserver(([entry]) => {
      if (entry?.isIntersecting && !this.#userPaused) video.play().catch(() => {});
      else video.pause();
      this.#syncToggle();
    });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
  }

  togglePlayback() {
    const video = this.#video;
    if (!video) return;

    this.#userPaused = !video.paused;
    if (this.#userPaused) video.pause();
    else video.play().catch(() => {});
    this.#syncToggle();
  }

  #syncToggle() {
    const { toggle } = this.refs;
    if (!toggle) return;

    const paused = this.#userPaused;
    toggle.setAttribute('aria-pressed', String(paused));
    toggle.setAttribute('aria-label', (paused ? toggle.dataset.labelPlay : toggle.dataset.labelPause) ?? '');
  }
}

/**
 * Counts a stat up from zero the first time it scrolls into view. The server-rendered text is the
 * final value, so nothing changes without JavaScript or with reduced motion.
 */
class MalaCountUp extends HTMLElement {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    const match = this.textContent?.trim().match(/^([^\d]*)([\d][\d.,]*)(.*)$/);
    if (!match || prefersReducedMotion()) return;

    const [, prefix = '', digits = '', suffix = ''] = match;
    const decimals = digits.includes('.') ? (digits.split('.')[1]?.length ?? 0) : 0;
    const target = Number(digits.replace(/,/g, ''));
    if (!Number.isFinite(target)) return;

    const format = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
      useGrouping: digits.includes(','),
    });

    this.#observer = new IntersectionObserver(([entry]) => {
      if (!entry?.isIntersecting) return;
      this.#observer?.disconnect();

      const start = performance.now();
      const duration = 1600;
      const tick = (/** @type {number} */ now) => {
        const progress = Math.min(1, (now - start) / duration);
        const eased = 1 - Math.pow(1 - progress, 4);
        this.textContent = `${prefix}${format.format(target * eased)}${suffix}`;
        if (progress < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }, { threshold: 0.6 });

    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }
}

/**
 * Feeds the pointer position to each bento tile or benefit item as `--mx` / `--my`, so CSS can paint a
 * light and a border glow that follow the cursor. One delegated listener serves the whole grid.
 */
class MalaSpotlight extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;
  #frame = 0;

  connectedCallback() {
    if (!window.matchMedia('(hover: hover)').matches) return;

    this.#abort = new AbortController();
    this.addEventListener('pointermove', this.#onPointerMove, { passive: true, signal: this.#abort.signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
    cancelAnimationFrame(this.#frame);
  }

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      for (const tile of this.querySelectorAll('.mala-bento__tile, .mala-perks__item')) {
        if (!(tile instanceof HTMLElement)) continue;

        const rect = tile.getBoundingClientRect();
        tile.style.setProperty('--mx', `${event.clientX - rect.left}px`);
        tile.style.setProperty('--my', `${event.clientY - rect.top}px`);
      }
    });
  };
}

/**
 * Loads Shopify product recommendations for its section when it nears the viewport and swaps in
 * the server-rendered carousel. Hides the section when there is nothing to recommend.
 */
class MalaRecommendations extends HTMLElement {
  /** @type {IntersectionObserver | undefined} */
  #observer;
  /** @type {AbortController | undefined} */
  #abort;

  connectedCallback() {
    if (this.querySelector('.mala-carousel') || !this.dataset.url) return;

    this.#observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        this.#observer?.disconnect();
        this.#load();
      },
      { rootMargin: '600px 0px' }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#abort?.abort();
  }

  async #load() {
    const url = this.dataset.url;
    if (!url) return;

    this.#abort = new AbortController();
    try {
      const response = await fetch(url, { signal: this.#abort.signal });
      if (!response.ok) throw new Error(`Recommendations request failed: ${response.status}`);

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = html.querySelector('mala-recommendations');
      if (fresh?.querySelector('.mala-carousel')) {
        // A fallback collection can include the product being viewed; drop it.
        for (const link of fresh.querySelectorAll('.mala-product-card__title a')) {
          if (link instanceof HTMLAnchorElement && new URL(link.href).pathname === window.location.pathname) {
            link.closest('li')?.remove();
          }
        }
        this.replaceChildren(...fresh.childNodes);
        return;
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error(error);
    }

    const section = this.closest('.shopify-section');
    if (section instanceof HTMLElement) section.hidden = true;
  }
}

const definitions = /** @type {const} */ ([
  ['mala-hero-slider', MalaHeroSlider],
  ['mala-carousel', MalaCarousel],
  ['mala-background-video', MalaBackgroundVideo],
  ['mala-count-up', MalaCountUp],
  ['mala-spotlight', MalaSpotlight],
  ['mala-recommendations', MalaRecommendations],
]);

for (const [name, element] of definitions) {
  if (!customElements.get(name)) customElements.define(name, element);
}
